import React, { Fragment } from 'react'
import { NextRouter, useRouter } from 'next/router'
import i18nextConfig from '../../next-i18next.config'
import {
  Transition,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react'
import Link from 'next/link'
import {
  languageFlagMapping,
  languageLabelMapping,
} from '../constants/languageMapping'
import useLocale from '../hooks/useLocale'
import { classNames } from '../lib/utils/classNames'
import userService from '../services/user.service'
import { useAuth } from '../providers/AuthProvider'
import { HeroIcons } from '../lib/utils/heroIcons'

interface LanguageSelectProps {
  minimalDisplay?: boolean
}

export default function LanguageSelect({
  minimalDisplay = false,
}: LanguageSelectProps) {
  const router: NextRouter = useRouter()
  const { locale, setLocale } = useLocale()
  const { isLoggedIn } = useAuth()

  const supportedLngs: string[] = i18nextConfig.i18n.locales

  const onLanguageChange = async (newLocale: string) => {
    await setLocale(newLocale)
    if (isLoggedIn) {
      await userService.updateUserPreference({
        // @ts-ignore
        lang: newLocale,
      })
    }
  }

  return (
    <div className="cursor-pointer">
      <Menu>
        <div className="relative">
          <MenuButton
            className={classNames(
              'relative flex cursor-default items-center text-sm text-neutralBlack hover:cursor-pointer',
              minimalDisplay
                ? 'h-10 w-10 justify-center rounded-md hover:bg-sheltersBlue/10'
                : 'w-full rounded-2xl border border-neutralGray/50 bg-white px-2 py-1 shadow-2xl',
            )}
          >
            <div className="pt-0.5">
              <HeroIcons.GlobeAltIcon className="h-5 w-5 text-sheltersBlue" />
            </div>
            {!minimalDisplay && (
              <div className="ml-auto pl-1.5 text-neutralBlack">
                {languageLabelMapping[locale]}
              </div>
            )}
          </MenuButton>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <MenuItems
              className={classNames(
                'hide-scrollbar absolute left-1/2 z-40 mt-1 max-h-60 -translate-x-1/2 overflow-y-scroll rounded-2xl border border-neutralGray/50 bg-neutralWhite text-sm shadow-lg',
                minimalDisplay ? 'w-max min-w-28' : 'top-9 w-max',
              )}
            >
              {supportedLngs.map((lng) => {
                const Flag = languageFlagMapping[lng]

                return (
                  <MenuItem key={lng} as={Fragment}>
                    <Link
                      href={router.asPath}
                      locale={lng}
                      onClick={() => onLanguageChange(lng)}
                      className={classNames(
                        'flex items-center px-3 py-0.5 hover:bg-gray-100',
                        minimalDisplay ? 'gap-2' : '',
                      )}
                    >
                      <span
                        className={classNames(
                          'text-neutralBlack',
                          minimalDisplay ? '' : 'pr-3 pt-1',
                        )}
                      >
                        <Flag className="h-5 w-5" />
                      </span>
                      <span
                        className={`block ${
                          locale === lng ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {languageLabelMapping[lng]}
                      </span>
                    </Link>
                  </MenuItem>
                )
              })}
            </MenuItems>
          </Transition>
        </div>
      </Menu>
    </div>
  )
}
